import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, UserPlus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toaster';
import { useAuthStore } from '@/auth';
import { clinicianHomeHospitalId } from '@/api/clinicianHospital';
import { useCreateClinician } from '@/hooks/queries';
import { extractApiErrorMessage } from '@/lib/apiError';

/**
 * Form for inviting a new clinician into the current hospital. Mirrors
 * v1's `addClinician.tsx` add mode — required: first/last/email; bio is
 * required by the server schema but we default to an empty string so
 * the form doesn't surface it (admins rarely fill it during onboarding).
 *
 * On success the server emails the new clinician a password-reset link
 * — that's how they get into the app the first time.
 */
type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const AddClinicianDialog = ({ open, onOpenChange }: Props) => {
  const { t } = useTranslation();
  const clinician = useAuthStore((s) => s.clinician);
  const hospitalId = clinicianHomeHospitalId(clinician) ?? null;
  const create = useCreateClinician();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhone('');
      setError(null);
      create.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (hospitalId == null) return;
    const fn = firstName.trim();
    const ln = lastName.trim();
    const em = email.trim();
    if (!fn || !ln) {
      setError(t('First and last name are required.'));
      return;
    }
    if (!EMAIL_RE.test(em)) {
      setError(t('Please enter a valid email'));
      return;
    }
    setError(null);
    try {
      await create.mutateAsync({
        hospitalId,
        body: {
          first_name: fn,
          last_name: ln,
          email: em,
          // Server requires `bio` per swagger:15394 — send empty string
          // so the form doesn't surface a field nobody fills in.
          bio: '',
          phone_number: phone.trim() || undefined,
        },
      });
      toast({
        title: t('Clinician added'),
        description: t('{{name}} will receive a password-reset email.', {
          name: `${fn} ${ln}`,
        }),
      });
      onOpenChange(false);
    } catch (err) {
      setError(extractApiErrorMessage(err) ?? t('Could not add clinician'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" />
            {t('Add clinician')}
          </DialogTitle>
          <DialogDescription>
            {t('Invite a colleague to your hospital. They will receive an email with a password setup link.')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ac-first">{t('First name')}</Label>
              <Input
                id="ac-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ac-last">{t('Last name')}</Label>
              <Input
                id="ac-last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ac-email">{t('Email')}</Label>
            <Input
              id="ac-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ac-phone">{t('Phone (optional)')}</Label>
            <Input
              id="ac-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={create.isPending}
            >
              {t('Cancel')}
            </Button>
            <Button type="submit" disabled={create.isPending || hospitalId == null}>
              {create.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
              {t('Add clinician')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
